import React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  Skeleton,
  Checkbox
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

function EmployeeTable({
  employees,
  loading,
  onEdit,
  onDelete,
  selectedIds = [],
  onSelectionChange
}) {
  const [page, setPage] = React.useState(1);

  const rowsPerPage = 50;

  const sortedEmployees = [...employees].sort(
    (a, b) => a.employee_id - b.employee_id
  );

  const startIndex = (page - 1) * rowsPerPage;

  const paginatedEmployees = sortedEmployees.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const totalPages = Math.ceil(
    employees.length / rowsPerPage
  );

  const allSelected = employees.length > 0 && selectedIds.length === employees.length;

  const isSelected = (employeeId) => selectedIds.includes(employeeId);

  const toggleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange?.(employees.map((employee) => employee.employee_id));
      return;
    }

    onSelectionChange?.([]);
  };

  const toggleSelectOne = (employeeId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedIds, employeeId]);
      return;
    }

    onSelectionChange?.(selectedIds.filter((id) => id !== employeeId));
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: "#f8fafc" }}>
              <TableCell
                sx={{
                  width: 56,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#475569"
                }}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedIds.length > 0 && !allSelected}
                  onChange={(event) => toggleSelectAll(event.target.checked)}
                />
              </TableCell>

              {[
                "Employee Name",
                "Email",
                "Department",
                "Role",
                "Manager",
                "Actions"
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#475569"
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && Array.from({ length: rowsPerPage }).map((_, index) => (
              <TableRow key={`employee-skeleton-${index}`}>
                <TableCell>
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Skeleton variant="circular" width={34} height={34} />
                    <Box>
                      <Skeleton variant="text" width={140} height={24} />
                      <Skeleton variant="text" width={100} height={18} />
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width="88%" height={24} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="rounded" width={90} height={24} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="rounded" width={84} height={24} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width={110} height={24} />
                </TableCell>

                <TableCell>
                  <Box display="flex" gap={1}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {!loading && paginatedEmployees.map((employee) => (
              <TableRow
                key={employee.employee_id}
                hover
                sx={{
                  "& td": {
                    borderBottom: "1px solid #f1f5f9"
                  }
                }}
              >
                <TableCell>
                  <Checkbox
                    checked={isSelected(employee.employee_id)}
                    onChange={(event) => toggleSelectOne(employee.employee_id, event.target.checked)}
                  />
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      src={employee.profile_picture || undefined}
                      sx={{
                        width: 34,
                        height: 34,
                        fontSize: 14
                      }}
                    >
                      {employee.employee_name?.charAt(0)}
                    </Avatar>

                    <Box>
                      <Typography fontSize={14} fontWeight={600}>
                        {employee.employee_name}
                      </Typography>

                      <Typography fontSize={12} color="#94a3b8">
                        {employee.designation || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography fontSize={14} color="#475569">
                    {employee.email}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    label={employee.department_name || "N/A"}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: 12,
                      background: "#eff6ff",
                      color: "#2563eb"
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Chip
                    label={employee.role_name || "member"}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: 11,
                      textTransform: "capitalize",
                      background: "#ecfeff",
                      color: "#155e75"
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Typography fontSize={13} color="#475569">
                    {employee.manager_name || "N/A"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box display="flex" gap={1}>
                    <IconButton
                      onClick={() => onEdit(employee)}
                      sx={{
                        background: "#eff6ff",
                        width: 32,
                        height: 32
                      }}
                    >
                      <EditIcon
                        fontSize="small"
                        sx={{
                          color: "#2563eb"
                        }}
                      />
                    </IconButton>

                    <IconButton
                      onClick={() => onDelete(employee)}
                      sx={{
                        background: "#fef2f2",
                        width: 32,
                        height: 32
                      }}
                    >
                      <DeleteIcon
                        fontSize="small"
                        sx={{
                          color: "#ef4444"
                        }}
                      />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {!loading && paginatedEmployees.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 6 }}>
                  <Box
                    sx={{
                      maxWidth: 520,
                      mx: "auto",
                      textAlign: "center",
                      borderRadius: 3,
                      border: "1px solid #dbeafe",
                      background: "linear-gradient(145deg, #f8fbff 0%, #eef6ff 100%)",
                      p: 4
                    }}
                  >
                    <Box
                      sx={{
                        width: 58,
                        height: 58,
                        mx: "auto",
                        mb: 1.8,
                        borderRadius: "50%",
                        background: "#dbeafe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#1d4ed8"
                      }}
                    >
                      <PeopleAltOutlinedIcon sx={{ fontSize: 30 }} />
                    </Box>

                    <Typography variant="h6" sx={{ color: "#1e3a8a", fontWeight: 700, mb: 0.7 }}>
                      No employees found
                    </Typography>

                    <Typography sx={{ color: "#475569", fontSize: 14, mb: 1.8 }}>
                      Try changing search text or add a new employee to start building your team list.
                    </Typography>

                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.7,
                        px: 1.4,
                        py: 0.6,
                        borderRadius: 999,
                        background: "#dbeafe",
                        color: "#1d4ed8",
                        fontSize: 12,
                        fontWeight: 700
                      }}
                    >
                      <AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />
                      Team roster is empty
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && employees.length > 0 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <Typography fontSize={13} color="#64748b">
            Showing {employees.length === 0 ? 0 : startIndex + 1}
            {" "}
            to
            {" "}
            {Math.min(startIndex + rowsPerPage, employees.length)}
            {" "}
            of {employees.length} results
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeftIcon />
            </Button>

            <Button
              variant="contained"
              size="small"
              sx={{
                minWidth: 32
              }}
            >
              {page}
            </Button>

            <Button
              size="small"
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRightIcon />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default EmployeeTable;
